// Detalle de una unidad de la búsqueda de /vehiculos: encabezado con placa, fuente y
// qué tan reciente es el último reporte; datos clave en tarjetas; mapa; y los datos
// crudos de la plataforma plegados y con buscador.
import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, IconButton, InputAdornment,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SpeedIcon from "@mui/icons-material/Speed";
import PlaceIcon from "@mui/icons-material/Place";
import RouterIcon from "@mui/icons-material/Router";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

import { Seccion, pieSx } from "../../../components/DialogoUi";
import MiniMapaVehiculo from "./MiniMapaVehiculo";
import JsonKeyValueTable from "./JsonKeyValueTable";

// "2026-10-26 16:55" (u otros formatos) -> Date, o null si no se puede leer
const leerFecha = (valor) => {
  if (!valor || typeof valor !== "string") return null;
  const d = new Date(valor.trim().replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

// Qué tan reciente es el último reporte: verde < 1 h, ámbar < 24 h, rojo más viejo
const frescura = (fecha) => {
  if (!fecha) return { color: "default", texto: "Sin fecha de reporte" };
  const min = Math.max(0, Math.round((Date.now() - fecha.getTime()) / 60000));
  const hace = min < 60 ? `hace ${min} min` : min < 1440 ? `hace ${Math.round(min / 60)} h` : `hace ${Math.round(min / 1440)} día${Math.round(min / 1440) === 1 ? "" : "s"}`;
  if (min < 60) return { color: "success", texto: `Reportando · ${hace}` };
  if (min < 1440) return { color: "warning", texto: `Último reporte ${hace}` };
  return { color: "error", texto: `Sin reportar ${hace}` };
};

const coordenadasValidas = (lat, lng) => {
  const la = Number(lat);
  const lo = Number(lng);
  return Number.isFinite(la) && Number.isFinite(lo) && !(la === 0 && lo === 0);
};

// Tarjeta de un dato clave
const Dato = ({ icono: Icono, etiqueta, children, accion }) => (
  <Box sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper", p: 1, minWidth: 0, display: "flex", gap: 1, alignItems: "flex-start" }}>
    <Box sx={{ width: 30, height: 30, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: "primary.main" }}>
      <Icono sx={{ fontSize: 17 }} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "text.secondary" }}>{etiqueta}</Typography>
      <Box sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}>{children}</Box>
    </Box>
    {accion}
  </Box>
);

export default function VehiculoDetalleDialog({
  open, onClose, vehiculo, razon, velocidad, puntoCercano, fuenteColor = "default", fuenteIcono, onReclamo, onVerModem,
}) {
  const [verExtras, setVerExtras] = useState(false);
  const [filtroExtras, setFiltroExtras] = useState("");
  const [copiado, setCopiado] = useState(false);

  const fecha = leerFecha(vehiculo?.fecha_ultimo_registro);
  const estado = frescura(fecha);
  const hayCoordenadas = coordenadasValidas(vehiculo?.latitud, vehiculo?.longitud);
  const coordenadas = hayCoordenadas ? `${Number(vehiculo.latitud).toFixed(6)}, ${Number(vehiculo.longitud).toFixed(6)}` : null;

  // Solo las claves/valores que contienen el texto buscado
  const extrasFiltrados = useMemo(() => {
    const extras = vehiculo?.extras;
    const q = filtroExtras.trim().toLowerCase();
    if (!q || !extras || typeof extras !== "object") return extras;
    const filtrar = (obj, prefijo = "") => Object.entries(obj).reduce((acc, [k, v]) => {
      const ruta = prefijo ? `${prefijo}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) {
        const hijo = filtrar(v, ruta);
        if (Object.keys(hijo).length) acc[k] = hijo;
      } else if (ruta.toLowerCase().includes(q) || String(v ?? "").toLowerCase().includes(q)) {
        acc[k] = v;
      }
      return acc;
    }, {});
    return filtrar(extras);
  }, [vehiculo, filtroExtras]);

  const copiarCoordenadas = () => {
    if (!coordenadas) return;
    navigator.clipboard?.writeText(coordenadas);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1500);
  };

  const cerrar = () => { setVerExtras(false); setFiltroExtras(""); onClose(); };

  if (!vehiculo) return null;
  const titulo = vehiculo.placa || vehiculo.alias || vehiculo.modem || "Unidad";

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="md" fullWidth>
      {/* Encabezado */}
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, borderBottom: 1, borderColor: "divider", bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.16 : 0.06) }}>
        <Box sx={{ width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: 1.5, bgcolor: "primary.main", color: "primary.contrastText" }}>
          <DirectionsCarIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: 20, fontWeight: 900, letterSpacing: 0.3, lineHeight: 1.2 }}>{titulo}</Typography>
            <Chip size="small" label={vehiculo.fuente} color={fuenteColor} icon={fuenteIcono || undefined} sx={{ height: 22, fontWeight: 700 }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
            {vehiculo.alias && vehiculo.alias !== titulo && (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{vehiculo.alias}</Typography>
            )}
            <Chip
              size="small"
              variant="outlined"
              color={estado.color}
              icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: estado.color === "default" ? "text.disabled" : `${estado.color}.main`, ml: "8px !important" }} />}
              label={estado.texto}
              sx={{ height: 22, fontSize: 11.5, fontWeight: 700 }}
            />
          </Stack>
        </Box>
        <IconButton size="small" onClick={cerrar} aria-label="Cerrar"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "background.default", p: 1.5, display: "grid", gap: 1.25, gridAutoRows: "max-content" }}>
        {/* Datos clave */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
          <Dato icono={AccessTimeIcon} etiqueta="Último reporte">{vehiculo.fecha_ultimo_registro || "—"}</Dato>
          <Dato icono={SpeedIcon} etiqueta="Velocidad">{velocidad == null || Number.isNaN(velocidad) ? "—" : `${velocidad} km/h`}</Dato>
          <Dato
            icono={RouterIcon}
            etiqueta="Módem"
            accion={vehiculo.modem && onVerModem ? (
              <Tooltip title="Ver en Centro de equipos">
                <IconButton size="small" onClick={() => onVerModem(vehiculo.modem)}><OpenInNewIcon sx={{ fontSize: 16 }} /></IconButton>
              </Tooltip>
            ) : null}
          >
            <Box component="span" sx={{ fontFamily: "monospace" }}>{vehiculo.modem || "—"}</Box>
          </Dato>
          <Dato icono={BusinessIcon} etiqueta="Razón / Cliente">{razon && razon !== "—" ? razon : "—"}</Dato>
          <Dato
            icono={PlaceIcon}
            etiqueta="Coordenadas"
            accion={coordenadas ? (
              <Tooltip title={copiado ? "Copiado" : "Copiar coordenadas"}>
                <IconButton size="small" onClick={copiarCoordenadas}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton>
              </Tooltip>
            ) : null}
          >
            <Box component="span" sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{coordenadas || "Sin ubicación"}</Box>
          </Dato>
          <Dato icono={PlaceIcon} etiqueta="Punto cercano">
            <Box component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{puntoCercano && puntoCercano !== "—" ? puntoCercano : "—"}</Box>
          </Dato>
        </Box>

        {/* Mapa */}
        <Seccion titulo="Ubicación actual" nota={hayCoordenadas ? null : "la plataforma no envió coordenadas"} sinCuerpo>
          <Box sx={{ p: 1 }}>
            <MiniMapaVehiculo
              key={`${vehiculo.latitud}-${vehiculo.longitud}`}
              latitud={vehiculo.latitud}
              longitud={vehiculo.longitud}
              alias={vehiculo.alias}
              velocidad={velocidad}
              initialTipoMapa="light"
            />
          </Box>
        </Seccion>

        {/* Datos crudos de la plataforma, plegados */}
        <Box sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setVerExtras((v) => !v)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setVerExtras((v) => !v))}
            sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.6, cursor: "pointer", bgcolor: "action.hover", borderLeft: "3px solid", borderLeftColor: "primary.main", "&:hover": { bgcolor: "action.selected" } }}
          >
            <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main", flex: 1 }}>
              Datos adicionales de {vehiculo.fuente}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{verExtras ? "Ocultar" : "Mostrar"}</Typography>
            <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary", transition: "transform .2s", transform: verExtras ? "rotate(180deg)" : "none" }} />
          </Box>
          <Collapse in={verExtras} unmountOnExit>
            <Box sx={{ p: 1.25, display: "grid", gap: 1 }}>
              <TextField
                size="small"
                placeholder="Filtrar por campo o valor…"
                value={filtroExtras}
                onChange={(e) => setFiltroExtras(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ maxWidth: 320, "& .MuiInputBase-root": { fontSize: 13 } }}
              />
              <Box sx={{ maxHeight: 360, overflow: "auto" }}>
                <JsonKeyValueTable data={extrasFiltrados} />
              </Box>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions sx={{ ...pieSx, gap: 1 }}>
        {hayCoordenadas && (
          <Button
            size="small"
            component="a"
            href={`https://www.google.com/maps?q=${vehiculo.latitud},${vehiculo.longitud}`}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Abrir en Google Maps
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={cerrar} sx={{ textTransform: "none" }}>Cerrar</Button>
        <Button
          size="small"
          variant="contained"
          color="warning"
          startIcon={<ReportProblemIcon fontSize="small" />}
          onClick={() => onReclamo(vehiculo)}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Emitir reclamo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
